<template>
  <div class="add-team-form">
    <div class="d-flex align-items-center mb-3">
      <button @click="openExistingMode" :class="accordion && !createMode ? 'btn-success' : 'btn-info'" class="btn btn-sm mr-2">
        <span>{{ $t('schedule.team.add_existing') }}</span></button>
      <add-team-from-application @applicationSelected="addTeamApplicationToSchedule" class="mr-2" :button-text="$t('schedule.team.add_teams_from_applications')"></add-team-from-application>
      <button data-testid="add-team-new-club" @click="openNewMode" :class="accordion && createMode ? 'btn-success' : 'btn-info'" class="btn btn-sm">
        <span>{{ $t('club.create_new') }}</span></button>
    </div>

    <modal name="club-duplicate-modal" :adaptive="true" height="auto" :width="440">
      <div v-if="duplicateClubs.length" class="p-4">
        <h5 class="mb-2">{{ $t('club.duplicateTitle') }}</h5>
        <p class="text-muted mb-2">{{ $t('club.duplicateUseExisting') }}</p>
        <div style="max-height: 260px; overflow-y: auto;" class="mb-3">
          <div
            v-for="club in duplicateClubs"
            :key="club.uuid"
            class="d-flex align-items-center p-2 rounded mb-2"
            style="border: 1px solid #e4e7ed; background: #f8f9fa; cursor: pointer;"
            @click="useExistingClub(club)"
          >
            <img v-if="club.logo" :src="club.logo" class="mr-2 rounded" style="width: 40px; height: 40px; object-fit: contain; flex-shrink: 0;" alt="">
            <div v-else class="mr-2 d-flex align-items-center justify-content-center bg-secondary text-white rounded" style="width: 40px; height: 40px; font-size: 11px; flex-shrink: 0;">
              {{ club.shortName }}
            </div>
            <div class="flex-grow-1 min-width-0">
              <div class="font-weight-bold">{{ club.name }}</div>
              <small class="text-muted">{{ club.shortName }}<template v-if="club.owner"> · {{ club.owner }}</template></small>
            </div>
            <i class="fa fa-chevron-right ml-2 text-muted"></i>
          </div>
        </div>
        <button class="btn btn-warning btn-block mb-2" @click="forceCreateClub">{{ $t('club.forceCreate') }}</button>
        <button class="btn btn-secondary btn-block" @click="$modal.hide('club-duplicate-modal')">{{ $t('cancel') }}</button>
      </div>
    </modal>

    <form :id="`add-team-group-form-${groupUuid}`" v-if="accordion" @submit.prevent="onAddTeamSubmit()">

      <!-- Club field with inline create toggle -->
      <div class="form-group" :class="{ 'has-error': clubHasError }">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <label class="mb-0">{{ $t('formly.fields.club') }}</label>
        </div>

        <multiselect
          v-if="!createMode"
          ref="clubSelect"
          :value="model.club"
          @input="onClubInput"
          :options="clubOptions"
          label="name"
          track-by="uuid"
          :internal-search="false"
          @search-change="searchClubs"
          :placeholder="$tc('select.option', 1)"
        >
          <template slot="option" slot-scope="{ option }">
            <span v-if="option._isAddNew" class="text-info">
              <i class="fa fa-plus-circle mr-1"></i>{{ $t('club.addNew') }}
            </span>
            <span v-else>{{ option.name }}</span>
          </template>
        </multiselect>
        <span v-if="!createMode && form.$errors && form.$errors.club" class="help-block form-text text-danger">
          {{ $t('validation.club.' + form.$errors.club[0]) }}
        </span>

        <div v-if="createMode" class="p-3 rounded" style="border: 1px solid #e4e7ed; background: #f8f9fa;">
          <div class="form-group formly-input" :class="{ 'has-error': form.$errors && form.$errors.name }">
            <label>{{ $t('formly.fields.name') }}</label>
            <input
              ref="nameInput"
              data-testid="add-team-club-name"
              type="text"
              class="form-control form-control-alt"
              v-model="newClub.name"
            >
            <span v-if="form.$errors && form.$errors.name" class="help-block form-text text-danger">
              {{ $t('validation.name.' + form.$errors.name[0]) }}
            </span>
          </div>
          <div class="form-group formly-input mb-0" :class="{ 'has-error': form.$errors && form.$errors.shortName }">
            <label>{{ $t('formly.fields.shortName') }}</label>
            <input
              type="text"
              class="form-control form-control-alt"
              data-testid="add-team-club-short-name"
              v-model="newClub.shortName"
            >
            <span v-if="form.$errors && form.$errors.shortName" class="help-block form-text text-danger">
              {{ $t('validation.shortName.' + form.$errors.shortName[0]) }}
            </span>
          </div>
        </div>
      </div>

      <formly-form :form="form" :model="model" :fields="fields"></formly-form>
      <button class="btn btn-success push-15"><i v-if="loading" class="fa fa-cog fa-spin"></i> <span
        v-if="!loading">{{ $t('save') }}</span></button>
    </form>
    <div class="divider"></div>
  </div>
</template>

<script>

import fields from './createTeamShortFormDefinition'
import TeamService from '@/services/team.service'
import ApplicationWindowService from '@/services/applicationWindow.service'
import AddTeamFromApplication from '@/views/schedules/createTeam/AddTeamFromApplication.vue'
import ApiService from '@/services/api.service'
import ClubService from '@/services/club.service'
import Multiselect from 'vue-multiselect'
import { debounce } from '@/lib/debounce'

export default {
  components: { AddTeamFromApplication, Multiselect },
  props: {
    scheduleUuid: String,
    groupUuid: String,
    initOpen: {
      type: Boolean,
      default: false
    }
  },
  mounted () {
    this.accordion = this.initOpen
    this.searchClubs('')
  },
  data () {
    return {
      accordion: false,
      loading: false,
      createMode: false,
      duplicateClubs: [],
      form: {},
      model: {
        club: null,
        suffix: null,
        out_of_competition: false
      },
      newClub: {
        name: '',
        shortName: ''
      },
      clubOptions: [],
      clubSearch: '',
      fields: fields
    }
  },
  computed: {
    clubHasError () {
      return this.form.$errors && (this.form.$errors.club || this.form.$errors.name)
    }
  },
  watch: {
    'newClub.name': debounce(function (val) {
      if (!val.trim()) {
        this.newClub.shortName = ''
        return
      }
      ClubService.suggestShortName(val).then((response) => {
        this.newClub.shortName = response.data.shortName
      })
    }, 300)
  },
  methods: {
    addTeamApplicationToSchedule ({ applicationWindow, teamApplication }) {
      ApplicationWindowService.createTeamFromTeamApplication(
        applicationWindow.uuid,
        teamApplication.uuid,
        {
          scheduleUuid: this.scheduleUuid,
          groupUuid: this.groupUuid
        })
        .then(() => {
          this.$emit('teamAdded')
        })
    },
    openExistingMode () {
      this.accordion = true
      this.createMode = false
      this.newClub = { name: '', shortName: '' }
    },
    openNewMode () {
      this.accordion = true
      this.createMode = true
      this.model.club = null
      this.$nextTick(() => this.$refs.nameInput && this.$refs.nameInput.focus())
    },
    toggleCreateMode () {
      this.createMode = !this.createMode
      if (this.createMode) {
        this.model.club = null
        this.$nextTick(() => this.$refs.nameInput && this.$refs.nameInput.focus())
      } else {
        this.newClub = { name: '', shortName: '' }
      }
    },
    searchClubs: debounce(function (q) {
      this.clubSearch = q || ''
      ApiService.get('api/v1/admin/search/clubs', { params: { search: q || '' } }).then((response) => {
        this.clubOptions = [...response.data, { _isAddNew: true, uuid: '__add_new__' }]
      })
    }, 200),
    onClubInput (option) {
      if (option && option._isAddNew) {
        this.openCreateModeFromSearch()
      } else {
        this.model.club = option
      }
    },
    openCreateModeFromSearch () {
      this.createMode = true
      this.model.club = null
      this.newClub.name = this.clubSearch
      if (this.clubSearch) {
        ClubService.suggestShortName(this.clubSearch).then((response) => {
          this.newClub.shortName = response.data.shortName
        })
      }
      this.$nextTick(() => this.$refs.nameInput && this.$refs.nameInput.focus())
    },
    focusClubSearch () {
      this.$nextTick(() => {
        if (this.createMode) {
          this.$refs.nameInput && this.$refs.nameInput.focus()
        } else {
          const el = this.$refs.clubSelect && this.$refs.clubSelect.$el.querySelector('.multiselect__input')
          el && el.focus()
        }
      })
    },
    onAddTeamSubmit (force = false) {
      this.loading = true
      this.form.$errors = {}

      const payload = {
        group: this.groupUuid,
        suffix: this.model.suffix,
        out_of_competition: this.model.out_of_competition
      }

      if (this.createMode) {
        payload.name = this.newClub.name
        payload.shortName = this.newClub.shortName
        if (force) payload.force = true
      } else {
        payload.club = this.model.club
      }

      TeamService.storeWithoutLoading(this.scheduleUuid, payload).then(() => {
        this.model = { club: null, suffix: null, out_of_competition: false }
        this.newClub = { name: '', shortName: '' }
        this.$emit('teamAdded')
      }).catch((err) => {
        if (err.response && err.response.status === 409 && err.response.data.type === 'duplicate_club') {
          this.duplicateClubs = err.response.data.clubs
          this.$modal.show('club-duplicate-modal')
        } else {
          this.form.$errors = err.response.data.errors
        }
      }).finally(() => {
        this.loading = false
      })
    },
    useExistingClub (club) {
      this.$modal.hide('club-duplicate-modal')
      this.model.club = club
      this.newClub = { name: '', shortName: '' }
      this.createMode = false
      this.duplicateClubs = []
      this.$nextTick(() => this.onAddTeamSubmit())
    },
    forceCreateClub () {
      this.$modal.hide('club-duplicate-modal')
      this.duplicateClubs = []
      this.onAddTeamSubmit(true)
    }
  }
}
</script>
